import React, { useCallback, useMemo, useRef } from 'react';
import type { InputRef } from 'antd';
import { AppButton, AppTextField } from '@/components/atoms';

const normalizePostalDigits = (val: string) =>
  String(val || '')
    .replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/\D/g, '');

export interface PostalCodeFieldsProps {
  postalCode1: string;
  postalCode2: string;
  onPostalCode1Change: (next: string) => void;
  onPostalCode2Change: (next: string) => void;
  /** Optional: when pc1 overflows, component can fill pc2 for you. */
  onPostalCode2Autofill?: (next: string) => void;
  onPostalCode1Blur?: () => void;
  onPostalCode2Blur?: () => void;
  disabled?: boolean;
  className?: string;
  /** Error message below pc1 input */
  errorPostalCode1?: string;
  /** Error message below pc2 input */
  errorPostalCode2?: string;
  /** Show 住所補助 button next to fields when provided */
  onSearch?: () => void;
  searchLabel?: string;
  searchLoading?: boolean;
  searchDisabled?: boolean;
  searchButtonClassName?: string;
  searchButtonSize?: 'small' | 'middle' | 'large';
  searchSlot?: React.ReactNode;
}

const PostalCodeFields: React.FC<PostalCodeFieldsProps> = ({
  postalCode1,
  postalCode2,
  onPostalCode1Change,
  onPostalCode2Change,
  onPostalCode2Autofill,
  onPostalCode1Blur,
  onPostalCode2Blur,
  disabled = false,
  className,
  errorPostalCode1,
  errorPostalCode2,
  onSearch,
  searchLabel = '住所補助',
  searchLoading = false,
  searchDisabled = false,
  searchButtonClassName,
  searchButtonSize,
  searchSlot,
}) => {
  const postalCode2Ref = useRef<InputRef | null>(null);
  const prevPc1LenRef = useRef<number>(postalCode1?.length ?? 0);

  const setPc2 = useCallback(
    (next: string) => {
      if (onPostalCode2Autofill) {
        onPostalCode2Autofill(next);
      } else {
        onPostalCode2Change(next);
      }
    },
    [onPostalCode2Autofill, onPostalCode2Change]
  );

  const pc1Value = useMemo(() => postalCode1 || '', [postalCode1]);
  const pc2Value = useMemo(() => postalCode2 || '', [postalCode2]);

  const hasSearch = searchSlot !== undefined || typeof onSearch === 'function';
  const hasError = !!(errorPostalCode1 || errorPostalCode2);

  const inputsRow = (
    <div className={className ?? 'flex items-center gap-2'}>
      <AppTextField
        value={pc1Value}
        placeholder="000"
        maxLength={3}
        inputMode="numeric"
        className="w-16"
        disabled={disabled}
        // data-skip-enter-nav
        // onPressEnter={() => onSearch?.()}
        onChange={e => {
          const digits = normalizePostalDigits((e.target as HTMLInputElement).value);
          const prevLen = prevPc1LenRef.current;
          const nextPc1 = digits.slice(0, 3);
          const overflow = digits.slice(3, 7);
          prevPc1LenRef.current = nextPc1.length;
          onPostalCode1Change(nextPc1);
          if (overflow.length > 0) {
            setPc2(overflow.slice(0, 4));
            queueMicrotask(() => postalCode2Ref.current?.focus?.());
          } else if (nextPc1.length === 3 && digits.length > prevLen) {
            queueMicrotask(() => postalCode2Ref.current?.focus?.());
          }
        }}
        onPaste={e => {
          const clip = normalizePostalDigits(e.clipboardData.getData('text') || '');
          if (clip.length < 2) return;
          e.preventDefault();
          const input = e.currentTarget;
          const current = normalizePostalDigits(input.value || '');
          const selStart = input.selectionStart ?? current.length;
          const selEnd = input.selectionEnd ?? current.length;
          const merged = `${current.slice(0, selStart)}${clip}${current.slice(selEnd)}`.replace(
            /\D/g,
            ''
          );
          const nextPc1 = merged.slice(0, 3);
          const overflow = merged.slice(3, 7);
          prevPc1LenRef.current = nextPc1.length;
          onPostalCode1Change(nextPc1);
          if (overflow.length > 0) {
            setPc2(overflow.slice(0, 4));
          }
          queueMicrotask(() => postalCode2Ref.current?.focus?.());
        }}
        onBlur={() => {
          const normalized = normalizePostalDigits(pc1Value).slice(0, 3);
          if (normalized !== pc1Value) onPostalCode1Change(normalized);
          prevPc1LenRef.current = normalized.length;
          onPostalCode1Blur?.();
        }}
      />
      <span>-</span>
      <AppTextField
        value={pc2Value}
        placeholder="0000"
        maxLength={4}
        inputMode="numeric"
        className="w-20"
        disabled={disabled}
        ref={postalCode2Ref}
        // data-skip-enter-nav
        // onPressEnter={() => onSearch?.()}
        onChange={e => {
          const normalized = normalizePostalDigits((e.target as HTMLInputElement).value).slice(
            0,
            4
          );
          onPostalCode2Change(normalized);
        }}
        onBlur={() => {
          const normalized = normalizePostalDigits(pc2Value).slice(0, 4);
          if (normalized !== pc2Value) onPostalCode2Change(normalized);
          onPostalCode2Blur?.();
        }}
      />
    </div>
  );

  if (!hasSearch && !hasError) return inputsRow;

  return (
    <div>
      <div className="flex items-center gap-2">
        {inputsRow}
        {searchSlot !== undefined
          ? searchSlot
          : onSearch && (
              <AppButton
                type="primary"
                htmlType="button"
                onClick={onSearch}
                loading={searchLoading}
                disabled={disabled || searchDisabled || searchLoading}
                className={searchButtonClassName}
                size={searchButtonSize}
              >
                {searchLabel}
              </AppButton>
            )}
      </div>
      {hasError && (
        <div className="flex gap-2 mt-1">
          <div className="w-16 shrink-0 text-sm leading-tight text-[var(--ant-color-error)] break-words">
            {errorPostalCode1}
          </div>
          <span className="invisible">-</span>
          <div className="w-20 shrink-0 text-sm leading-tight text-[var(--ant-color-error)] break-words">
            {errorPostalCode2}
          </div>
        </div>
      )}
    </div>
  );
};

export default PostalCodeFields;
